import {
  Scene,
  UniversalCamera,
  Vector3,
} from '@babylonjs/core';

export class FirstPersonCamera {
  private camera: UniversalCamera;
  private scene: Scene;

  public yaw: number = 0;   // Horizontal rotation
  public pitch: number = 0; // Vertical rotation

  private readonly minPitch: number = -Math.PI / 2 + 0.1;
  private readonly maxPitch: number = Math.PI / 2 - 0.1;
  private readonly sensitivity: number = 0.002;

  constructor(scene: Scene, _canvas: HTMLCanvasElement) {
    this.scene = scene;

    this.camera = new UniversalCamera(
      'FirstPersonCamera',
      new Vector3(0, 10, 0),
      scene
    );

    this.camera.minZ = 0.1;
    this.camera.maxZ = 500;
    this.camera.fov = 1.2; // ~70 degrees

    // Disable default controls - we'll handle input ourselves
    this.camera.inputs.clear();
  }

  public rotate(deltaX: number, deltaY: number): void {
    this.yaw -= deltaX * this.sensitivity;
    this.pitch -= deltaY * this.sensitivity;

    // Clamp pitch
    this.pitch = Math.max(this.minPitch, Math.min(this.maxPitch, this.pitch));

    // Normalize yaw
    while (this.yaw > Math.PI) this.yaw -= Math.PI * 2;
    while (this.yaw < -Math.PI) this.yaw += Math.PI * 2;

    this.updateRotation();
  }

  private updateRotation(): void {
    // Calculate look direction from yaw and pitch
    const direction = new Vector3(
      Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      Math.cos(this.yaw) * Math.cos(this.pitch)
    );

    this.camera.setTarget(this.camera.position.add(direction));
  }

  public setPosition(position: Vector3): void {
    this.camera.position = position.clone();
    this.updateRotation();
  }

  public getForwardDirection(): Vector3 {
    // Forward direction on XZ plane (ignoring pitch)
    return new Vector3(
      Math.sin(this.yaw),
      0,
      Math.cos(this.yaw)
    ).normalize();
  }

  public getRightDirection(): Vector3 {
    return new Vector3(
      Math.cos(this.yaw),
      0,
      -Math.sin(this.yaw)
    ).normalize();
  }

  public getLookDirection(): Vector3 {
    return new Vector3(
      Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      Math.cos(this.yaw) * Math.cos(this.pitch)
    ).normalize();
  }

  public getCamera(): UniversalCamera {
    return this.camera;
  }

  public setActive(active: boolean): void {
    if (active) {
      this.scene.activeCamera = this.camera;
    }
  }

  public getYaw(): number {
    return this.yaw;
  }

  public setYaw(yaw: number): void {
    this.yaw = yaw;
    this.updateRotation();
  }

  public dispose(): void {
    this.camera.dispose();
  }
}
